/** @import {ProductModelElement} from "#app_lib/app_element_classes.ts" */

// Try to not conflict with existing functions
function getFocusableElements(/** @type {HTMLElement} */ container) {
  /** @type {NodeListOf<HTMLElement>} */
  const elms = container.querySelectorAll(
    "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), " +
      "[draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
  );
  return Array.from(elms);
}

/**
 * @type {{
 *   focusin?(event: Event): void;
 *   focusout?(event: Event): void;
 *   keydown?(event: KeyboardEvent): void;
 * }}
 */
const trapFocusHandlers = {};

function trapFocus(/** @type {HTMLElement} */ container, /** @type {HTMLElement} */ elementToFocus = container) {
  const elements = getFocusableElements(container);
  if (elements.length === 0) {
    console.error("No elements found");
    return;
  }
  const first = elements[0];
  const last = elements[elements.length - 1];
  removeTrapFocus();
  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) {
      return;
    }
    if (trapFocusHandlers.keydown !== undefined) {
      document.addEventListener("keydown", trapFocusHandlers.keydown);
    }
  };
  trapFocusHandlers.focusout = () => {
    if (trapFocusHandlers.keydown !== undefined) {
      document.removeEventListener("keydown", trapFocusHandlers.keydown);
    }
  };
  trapFocusHandlers.keydown = (event) => {
    // If not TAB key
    if (event.code.toUpperCase() !== "TAB") {
      return;
    }
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };
  if (trapFocusHandlers.focusout !== undefined) {
    document.addEventListener("focusout", trapFocusHandlers.focusout);
  }
  if (trapFocusHandlers.focusin !== undefined) {
    document.addEventListener("focusin", trapFocusHandlers.focusin);
  }
  elementToFocus.focus();
  if (elementToFocus.tagName === "INPUT") {
    // NOTE: We cant use type guards so we have to double cast
    /** @type {any} */
    const elm1 = elementToFocus;
    /** @type {HTMLInputElement} */
    const elm2 = elm1;
    if (["search", "text", "email", "url"].includes(elm2.type) && elm2.value !== "") {
      elm2.setSelectionRange(0, elm2.value.length);
    }
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (_e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  /** @type {HTMLElement | null} */
  let currentFocusedElement = null;
  /** @type {boolean | null} */
  let mouseClick = null;
  globalThis.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });
  globalThis.addEventListener("mousedown", (_event) => {
    mouseClick = true;
  });
  globalThis.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement !== null) {
        currentFocusedElement.classList.remove("focused");
      }
      if (mouseClick) {
        return;
      }
      // NOTE: We need this to be HTMLElement not Element
      /** @type {any} */
      const activeElm = document.activeElement;
      /** @type {HTMLElement | null} */
      currentFocusedElement = activeElm;
      if (currentFocusedElement !== null) {
        currentFocusedElement.classList.add("focused");
      }
    },
    true,
  );
}

/**
 * @param {HTMLElement | null} [elementToFocus]
 */
function removeTrapFocus(elementToFocus) {
  if (trapFocusHandlers.focusin !== undefined) {
    document.removeEventListener("focusin", trapFocusHandlers.focusin);
  }
  if (trapFocusHandlers.focusout !== undefined) {
    document.removeEventListener("focusout", trapFocusHandlers.focusout);
  }
  if (trapFocusHandlers.keydown !== undefined) {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  }
  if (elementToFocus !== null && elementToFocus !== undefined) {
    elementToFocus.focus();
  }
}

function pauseAllMedia() {
  /** @type {NodeListOf<HTMLObjectElement>} */
  const elms1 = document.querySelectorAll(".js-youtube");
  for (const video of elms1) {
    if (video.contentWindow === null) {
      console.error("Youtube video not ready");
    } else {
      video.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: "pauseVideo",
          args: "",
        }),
        "*",
      );
    }
  }
  /** @type {NodeListOf<HTMLObjectElement>} */
  const elms2 = document.querySelectorAll(".js-vimeo");
  for (const video of elms2) {
    if (video.contentWindow === null) {
      console.error("Vimeo video not ready");
    } else {
      video.contentWindow.postMessage(
        JSON.stringify({
          method: "pause",
        }),
        "*",
      );
    }
  }
  const elms3 = document.querySelectorAll("video");
  for (const video of elms3) {
    video.pause();
  }
  /** @type {NodeListOf<ProductModelElement>} */
  const elms4 = document.querySelectorAll("product-model");
  for (const model of elms4) {
    if (model.modelViewerUI) {
      model.modelViewerUI.pause();
    }
  }
}

// Test updated native components
class ModalDialog extends HTMLElement {
  /** @type {boolean} */
  moved = false;
  /** @type {HTMLElement | null} */
  openedBy = null;

  // Modal/Popup component
  // Usage:
  //   Create a <modal-dialog></modal-dialog> and inside place an element with a
  //   role="dialog" attribute which will hold your content. Any elements added
  //   with an id starting with "ModalClose-" are ready to close your modal/popup
  //   Alternatively you can send a show() or hide() to this element
  constructor() {
    super();
    /** @type {HTMLElement | null} */
    const closeElm1 = this.querySelector('[id^="ModalClose-"]');
    if (closeElm1 === null) {
      console.error("No close button");
    } else {
      closeElm1.addEventListener("click", this.hide.bind(this));
    }
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") {
        this.hide();
      }
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && event.target !== null) {
          // NOTE: TS JSDoc doesnt give us a good way to get Event.target as a proper HTMLElement
          /** @type {any} */
          const targ1 = event.target;
          /** @type {HTMLElement} */
          const targ = targ1;
          if (!targ.closest("deferred-media, product-model")) {
            this.hide();
          }
        }
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) {
          this.hide();
        }
      });
    }
  }

  connectedCallback() {
    if (this.moved) {
      return;
    }
    this.moved = true;
    document.body.appendChild(this);
  }

  show(/** @type {HTMLElement} */ opener) {
    this.openedBy = opener;
    // Enable the line below if we're going to add a DeferredMedia JS component, helps lazy loading videos, embeds, etc.
    // const popup = this.querySelector('.template-popup');
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    // Enable the line below if we're going to add a DeferredMedia JS component, helps lazy loading videos, embeds, etc.
    // if (popup) popup.loadContent();
    /** @type {HTMLElement | null} */
    const dialogElm = this.querySelector('[role="dialog"]');
    if (dialogElm === null) {
      console.error("No dialog element found");
    } else {
      trapFocus(this, dialogElm);
    }
    pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  // Wrapper for buttons that sends a .show() to a ModalDialog component.
  // Usage:
  //   Create a <modal-opener></modal-opener> with a data-modal attribute pointing to the
  //   CSS selector of the modal you wish to open. Add your button inside the modal-opener
  //   and it will open the specified ModalDialog component.
  constructor() {
    super();
    const button = this.querySelector("button");
    if (button === null) {
      return;
    }
    button.addEventListener("click", () => {
      const attr1 = this.getAttribute("data-modal");
      if (attr1 !== null) {
        /** @type {ProductModelElement | null} */
        const modal = document.querySelector(attr1);
        if (modal !== null) {
          modal.show(button);
        }
      }
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class SliderComponent extends HTMLElement {
  /** @type {HTMLElement | null} */
  slider = null;
  /** @type {NodeListOf<HTMLElement> | null} */
  sliderItems = null;
  /** @type {boolean} */
  enableSliderLooping = false;
  /** @type {HTMLElement | null} */
  currentPageElement = null;
  /** @type {HTMLElement | null} */
  pageTotalElement = null;
  /** @type {HTMLButtonElement | null} */
  prevButton = null;
  /** @type {HTMLButtonElement | null} */
  nextButton = null;
  /** @type {HTMLElement | null} */
  customScrollbar = null;
  /** @type {number | null} */
  sliderItemOffset = null;
  /** @type {number | null} */
  currentPage = null;
  /** @type {number | null} */
  totalPages = null;
  /** @type {HTMLElement[]} */
  sliderItemsToShow = [];
  /** @type {string | null} */
  loopType = null;

  // Slider Component that is by default size and breakpoint agnostic, works with native scroll
  // Usage:
  //   Create a <slider-component></slider-component> and inside place a element with any ID starting with
  //   "Slider-" and class "slider--everywhere"
  //   Add slides, each one with any ID starting with "Slide-"
  //   If you want buttons, add button elements with a "name" attributed assigned to "previous" or "next"
  //   If you want an automatically updated numeric counter add two elements with classes ".slider-counter--current"
  //   and ".slider-counter--total"
  //   Add the script that contains this definition as deferred or after load so that the constructor
  //   initializes properly after the elements exist in the DOM
  //   Add the CSS base file related to this component
  //   With CSS set the "Slider-" as flex and set any size you want for the slides or for each slide individually,
  //   if the Slide becomes wider than the size of the slider-component you will be able to scroll or use the buttons
  // TODO: looptypes currently only working with 1 slide per page
  // TODO: extend to add centering feature (right now it default to left align on first slide)
  // TODO: autoplay and controls
  constructor() {
    super();
    /** @type {HTMLElement | null} */
    this.slider = this.querySelector('[id^="Slider-"]');
    /** @type {NodeListOf<HTMLElement>} */
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    /** @type {boolean} */
    this.enableSliderLooping = this.hasAttribute("data-loop");
    /** @type {HTMLElement | null} */
    this.currentPageElement = this.querySelector(".slider-counter--current");
    /** @type {HTMLElement | null} */
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    /** @type {HTMLButtonElement | null} */
    this.prevButton = this.querySelector('button[name="previous"]');
    /** @type {HTMLButtonElement | null} */
    this.nextButton = this.querySelector('button[name="next"]');
    /** @type {HTMLElement | null} */
    this.customScrollbar = this.querySelector(".scrollbar .scroll-current");
    if (this.slider === null) {
      return;
    }
    if (this.customScrollbar && this.slider.clientWidth < this.slider.scrollWidth) {
      const val1 = (this.slider.clientWidth * 100) / this.slider.scrollWidth;
      this.customScrollbar.style.width = `${val1}%`;
    }
    this.initPages();
    const resizeObserver = new ResizeObserver((_entries) => this.initPages());
    resizeObserver.observe(this.slider);
    this.slider.addEventListener("scroll", this.update.bind(this));
    if (this.nextButton === null) {
      return;
    }
    if (this.prevButton !== null) {
      this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    }
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
    if (this.enableSliderLooping) {
      // Directly reading and assigning the attribute was easier but used
      // switch/case to show and enforce available options
      switch (this.dataset.loop) {
        case "infinite":
        case "slide-back":
          this.loopType = this.dataset.loop;
          break;
        default:
          this.loopType = "none";
          break;
      }
    }
  }

  initPages() {
    if (this.slider === null) {
      console.error("No slider element found");
      return;
    }
    if (this.sliderItems === null) {
      console.error("No slider items found");
      return;
    }
    if (this.customScrollbar && this.slider.clientWidth < this.slider.scrollWidth) {
      const val1 = (this.slider.clientWidth * 100) / this.slider.scrollWidth;
      this.customScrollbar.style.width = `${val1}%`;
    }
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) {
      return;
    }
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (this.slider === null) {
      console.error("No slider element found");
      return;
    }
    if (
      this.customScrollbar &&
      this.customScrollbar.parentElement &&
      this.slider.clientWidth < this.slider.scrollWidth
    ) {
      // slides can freely move so we have to read scale down the offset?
      // or maybe we just snap them and add smooth transition for better performance
      // rule of 3: smallOffset = (bigOffset * smallFullSize) / bigFullSize
      const val1 = (this.slider.scrollLeft * this.customScrollbar.parentElement.clientWidth) / this.slider.scrollWidth;
      this.customScrollbar.style.transform = `translateX(${val1}px)`;
    }
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.nextButton) {
      return;
    }
    const previousPage = this.currentPage;
    if (this.sliderItemOffset !== null) {
      this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    }
    if (this.currentPageElement !== null && this.currentPage !== null) {
      this.currentPageElement.textContent = this.currentPage.toString();
    }
    if (this.pageTotalElement !== null && this.totalPages !== null) {
      this.pageTotalElement.textContent = this.totalPages.toString();
    }
    if (this.currentPage !== null && this.currentPage !== previousPage) {
      if (this.currentPage - 1 > this.sliderItemsToShow.length) {
        console.error("Current page is out of bounds");
      } else {
        this.dispatchEvent(
          new CustomEvent("slideChanged", {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow[this.currentPage - 1],
            },
          }),
        );
      }
    }
    if (this.enableSliderLooping) {
      return;
    }
    if (this.prevButton !== null) {
      if (
        this.sliderItemsToShow.length > 0 &&
        this.isSlideVisible(this.sliderItemsToShow[0]) &&
        this.slider.scrollLeft === 0
      ) {
        this.prevButton.setAttribute("disabled", "disabled");
      } else {
        this.prevButton.removeAttribute("disabled");
      }
    }
    if (
      this.sliderItemsToShow.length > 0 &&
      this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(/** @type {HTMLElement} */ element, /** @type {number} */ offset = 0) {
    if (this.slider === null) {
      return false;
    }
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(/** @type {MouseEvent} */ event) {
    event.preventDefault();
    if (this.slider === null) {
      console.error("No slider element found");
      return;
    }
    if (this.sliderItems === null) {
      console.error("No slider items found");
      return;
    }
    if (this.sliderItemOffset === null) {
      console.error("No slider item offset found");
      return;
    }
    // NOTE: We cant cast current target easily so we use `any` intermediate
    /** @type {any} */
    const targ1 = event.currentTarget;
    /** @type {HTMLInputElement} */
    const targ = targ1;
    const origStep = targ.dataset.step;
    const step = origStep === "" || origStep === undefined ? 1 : Number(origStep);
    if (this.enableSliderLooping && this.loopType === "slide-back") {
      if (this.currentPage === 1 && targ.name === "previous") {
        this.slideScrollPosition = this.slider.scrollLeft + this.sliderItems.length * this.sliderItemOffset;
      } else if (this.currentPage === this.totalPages && targ.name === "next") {
        this.slideScrollPosition = this.slider.scrollLeft - this.sliderItems.length * this.sliderItemOffset;
      } else {
        this.slideScrollPosition =
          targ.name === "next"
            ? this.slider.scrollLeft + step * this.sliderItemOffset
            : this.slider.scrollLeft - step * this.sliderItemOffset;
      }
    } else {
      this.slideScrollPosition =
        targ.name === "next"
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
    }
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(/** @type {number} */ position) {
    if (this.slider === null) {
      console.error("No slider element found");
      return;
    }
    this.slider.scrollTo({
      left: position,
    });
  }
}
customElements.define("slider-component", SliderComponent);

// TODO: smooth anchor scrolling but rebuilt with modern native browser API
// biome-ignore lint/correctness/noUnusedVariables: This is probably used somewhere?
function post(
  /** @type {string} */ route,
  /** @type {{[key: string]: unknown} | undefined} */ content = undefined,
  /** @type {((obj: object) => void) | undefined} */ successCallback = undefined,
  /** @type {((obj: object) => void) | undefined} */ errorCallback = undefined,
  /** @type {(() => void) | undefined} */ alwaysCallback = undefined,
) {
  const content1 = content || {};
  // Simple network POST request helper
  // Usage:
  //   route: the route/path/resource
  //   content (optional): a javascript object containing data to be sent to the route
  //   successCallback (optional): a function you want to run if the network request is successful
  //   errorCallback (optional): a function you want to run if the network request results in error
  //   alwaysCallback (optional): a function you want to run regardless of the result of the network request
  const controller = new AbortController();
  return fetch(route, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    mode: "cors",
    body: JSON.stringify(content1),
    signal: controller.signal,
  })
    .then((/** @type {{json(): object; ok: boolean}} */ response) => {
      if (response.ok) {
        if (successCallback !== undefined) {
          successCallback(response.json());
        }
      } else {
        if (errorCallback !== undefined) {
          errorCallback(response.json());
        }
      }
      if (alwaysCallback !== undefined) {
        alwaysCallback();
      }
    })
    .catch((e) => {
      if (e.name !== "AbortError") {
        if (errorCallback) {
          errorCallback(JSON.parse(""));
        }
      }
      if (alwaysCallback) {
        alwaysCallback();
      }
    });
}

/**
 * Set the last column class for the last column in a flexbox or css grid row
 * This can eventually be replaced by css4 :last-col selector
 */
(function initRsGlobals() {
  function setLastCols() {
    function setLastCol(/** @type {HTMLElement} */ item) {
      item.classList.add("last-col");
    }

    function isItemLeftOf(/** @type {HTMLElement} */ item1, /** @type {HTMLElement} */ item2) {
      const compItem2Width = getComputedStyle(item2).getPropertyValue("width");
      const hasCompItem2Width = compItem2Width.match(/^[\d\.-]+px$/) !== null;
      const item2Width = hasCompItem2Width ? Math.floor(Number(compItem2Width.replace(/px$/, ""))) : item2.offsetWidth;
      return item1.offsetLeft < item2.offsetLeft + item2Width;
    }

    function findContainers() {
      // NOTE: We cant pass generics here so we have to do a hacky cast
      /** @type {any} */
      const elms1 = document.getElementsByClassName("css-set-last-cols");
      /** @type {HTMLCollectionOf<HTMLElement>} */
      const elms2 = elms1;
      const containers = Array.from(elms2);
      for (const container of containers) {
        assignLastColsForCont(container);
      }
    }

    function assignLastColsForCont(/** @type {HTMLElement} */ container) {
      // NOTE: We have coerce type to html elements
      /** @type {any} */
      const elms1 = container.children;
      /** @type {HTMLCollectionOf<HTMLElement>} */
      const elms2 = elms1;
      const items = Array.from(elms2);
      /** @type {HTMLElement | null} */
      let lastItem = null;
      if (items.length === 0) {
        return;
      }
      // setLastCol(items[items.length - 1]);
      for (const item of items) {
        if (lastItem !== null && isItemLeftOf(item, lastItem)) {
          setLastCol(lastItem);
        }
        lastItem = item;
      }
    }
    findContainers();
  }

  globalThis.document.addEventListener("DOMContentLoaded", setLastCols);
  globalThis.addEventListener("resize", setLastCols);
})();
